import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT, OCPP } from "../env";
import type { Charger, Driver } from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";

interface Props {
  charger: Charger | null;
  onClose: () => void;
}

export default function ChargerActionsDialog({ charger, onClose }: Props) {
  const api = useAuthApi();
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Remote start state
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | "">("");
  const [connectorId, setConnectorId] = useState("1");

  const toast = useToast();

  const loadDrivers = useCallback(async () => {
    try {
      const data = await api<DriverListResponse>(`${MGMT}/drivers`);
      setDrivers(fullDriversFromResponse(data).filter((d) => d.status === "active"));
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  }, [toast]);

  useEffect(() => {
    if (startDialogOpen) loadDrivers();
  }, [startDialogOpen, loadDrivers]);

  if (!charger) return null;

  const disabled = !charger.is_connected || busy;

  const call = async (path: string, body: unknown) => {
    setBusy(true);
    setResult(null);
    try {
      const tenantSlug = charger.tenant_slug;
      if (!tenantSlug) throw new Error("Charger has no tenant — cannot send action");
      const res = await api<unknown>(
        `${OCPP}/tenants/${tenantSlug}/chargers/${charger.charge_point_id}/actions/${path}`,
        { method: "POST", body: JSON.stringify(body) }
      );
      setResult({ ok: true, message: `Status: ${JSON.stringify(res ?? "OK")}` });
    } catch (e) {
      setResult({ ok: false, message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleRemoteStart = async () => {
    if (!selectedDriverId) {
      toast.show("Select a driver", "error");
      return;
    }
    setStartDialogOpen(false);
    await call("remote-start", {
      driver_id: selectedDriverId,
      connector_id: parseInt(connectorId, 10) || 1,
    });
  };

  const remoteStop = () => {
    const tx = window.prompt("Transaction ID:");
    if (!tx) return;
    call("remote-stop", { transaction_id: parseInt(tx, 10) });
  };

  const reset = (type: "Soft" | "Hard") => {
    if (type === "Hard" && !window.confirm("Hard reset will reboot the charger. Continue?")) {
      return;
    }
    call("reset", { type });
  };

  const clearCache = () => {
    if (!window.confirm("Clear authorization cache on the charger?")) return;
    call("clear-cache", {});
  };

  const close = () => {
    setResult(null);
    onClose();
  };

  return (
    <>
      <Dialog open onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>
          Actions: <span style={{ fontFamily: "monospace" }}>{charger.charge_point_id}</span>
        </DialogTitle>
        <DialogContent>
          {!charger.is_connected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Charger is offline. Actions are disabled.
            </Alert>
          )}
          <Stack spacing={1}>
            <Button
              variant="outlined"
              disabled={disabled}
              onClick={() => {
                setSelectedDriverId("");
                setConnectorId("1");
                setStartDialogOpen(true);
              }}
            >
              Remote Start
            </Button>
            <Button variant="outlined" disabled={disabled} onClick={remoteStop}>
              Remote Stop
            </Button>
            <Button variant="outlined" disabled={disabled} onClick={() => reset("Soft")}>
              Soft Reset
            </Button>
            <Button variant="outlined" color="warning" disabled={disabled} onClick={() => reset("Hard")}>
              Hard Reset
            </Button>
            <Button variant="outlined" disabled={disabled} onClick={clearCache}>
              Clear Cache
            </Button>
          </Stack>
          {result && (
            <Alert severity={result.ok ? "success" : "error"} sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {result.message}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Remote Start sub-dialog */}
      <Dialog open={startDialogOpen} onClose={() => setStartDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remote Start Transaction</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
              The driver's first active RFID card will be used. A matching contract must exist.
            </Alert>
            <FormControl fullWidth required>
              <InputLabel>Driver</InputLabel>
              <Select
                value={selectedDriverId}
                label="Driver"
                onChange={(e) => setSelectedDriverId(e.target.value as number)}
              >
                {drivers.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Connector ID"
              type="number"
              value={connectorId}
              onChange={(e) => setConnectorId(e.target.value)}
              inputProps={{ min: 1 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRemoteStart} disabled={!selectedDriverId}>
            Start
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
